// LMS Connector admin endpoints (docs/03 §Admin conventions). The raw
// access token rides Authorization per the platform convention; the docs/09
// error envelope is surfaced as LmsAdminError.
import { getAccessToken } from '../../storage/auth'

const base = () => process.env.REACT_APP_API_LMS

export class LmsAdminError extends Error {
  constructor(status, envelope) {
    const error = (envelope && envelope.error) || {}
    super(error.message || `LMS admin request failed (${status})`)
    this.status = status
    this.code = error.code || 'INTERNAL_ERROR'
    this.details = error.details || {}
    // Whether the message is a sentence for people: the LMS's envelope (a code
    // and its message), not a proxy's bare status read as "request failed".
    this.readable = !!(error.code && error.message)
  }
}

// What a failure says to a person: the LMS's own sentence when its envelope
// carried one (a code and its message), else the page's own words. A proxy's
// bare 502 and a network failure have no sentence of the LMS's, and their text
// ("LMS admin request failed (502)", "Failed to fetch") is not for people.
export const failureText = (error, fallback) => (error && error.readable ? error.message : fallback)

// A failed read in words: the service's own sentence after what could not be
// read, else only that it could not be, with the wait that is the usual remedy.
export const readFailureText = (what, error) =>
  error && error.readable ? `${what} could not be read: ${error.message}` : `${what} could not be read. Try again in a moment.`

// How long a read may wait for the LMS service before the page is told it did
// not answer. Only a GET has one: a write the browser gave up on may still be
// carried out by the server, so its outcome would be unknown, and the person
// would not know whether to try again.
export const GET_TIMEOUT_MS = 30000

// How long the staff-only read may wait (lms-access.js).
export const ACCESS_TIMEOUT_MS = 4000

const timeoutError = () =>
  new LmsAdminError(0, { error: { code: 'REQUEST_TIMEOUT', message: 'The LMS service did not answer in time.' } })

const request = async (method, path, body, timeoutMs = GET_TIMEOUT_MS) => {
  const readOnly = method === 'GET'
  const controller = readOnly ? new AbortController() : null
  let timedOut = false
  // The timer covers the answer and its body (the abort ends both); it is
  // cleared once the body is read.
  const timer = readOnly
    ? setTimeout(() => {
        timedOut = true
        controller.abort()
      }, timeoutMs)
    : null
  try {
    const response = await fetch(`${base()}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: getAccessToken()
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller ? controller.signal : undefined
    })
    if (response.status === 204) {
      return null
    }
    let json = null
    try {
      json = await response.json()
    } catch {}
    if (timedOut) throw timeoutError()
    if (!response.ok) {
      throw new LmsAdminError(response.status, json)
    }
    return json
  } catch (e) {
    if (timedOut) throw timeoutError()
    throw e
  } finally {
    if (timer) clearTimeout(timer)
  }
}

export const lmsAdmin = {
  // Whether the LMS admin is staff-only on this environment (lms-access.js).
  // A short wait: nothing waits for the answer (it is staff-only until it
  // arrives), so a slow LMS must not hold a connection for 30 seconds.
  getAccess: () => request('GET', '/admin/lms/access', undefined, ACCESS_TIMEOUT_MS),
  listConnections: () => request('GET', '/admin/lms/connections'),
  createConnection: payload => request('POST', '/admin/lms/connections', payload),
  getConnection: id => request('GET', `/admin/lms/connections/${id}`),
  patchConnection: (id, payload) => request('PATCH', `/admin/lms/connections/${id}`, payload),
  // What the connection's launches ran into that its admin can fix: the cmi5
  // home pages refused in the last 30 days (Phase C prep, plan Task 4). An LTI
  // 1.3 connection's also carry lti_refusals, lti_score_failures and
  // lti_score_applied_409 (the grades the LMS answered "already applied" to).
  getConnectionDiagnostics: id => request('GET', `/admin/lms/connections/${id}/diagnostics`),
  archiveConnection: id => request('DELETE', `/admin/lms/connections/${id}`),
  pauseConnection: id => request('POST', `/admin/lms/connections/${id}/pause`),
  resumeConnection: id => request('POST', `/admin/lms/connections/${id}/resume`),
  rotateSigningKey: id => request('POST', `/admin/lms/connections/${id}/signing-key/rotate`),
  revokeSigningKey: id => request('POST', `/admin/lms/connections/${id}/signing-key/revoke`),
  // An LTI 1.3 connection (delivery lti13, Phase L): the registration. A GET
  // answers { registered, tool_handle, tool_urls { login_initiation_url,
  // launch_url, deep_linking_url, jwks_url }, tool_key { id, kid, issued_at,
  // previous_valid_until }, ... } from the day the connection is made, with the
  // platform's own fields (issuer, client_id, deployment_ids, auth_login_url,
  // token_url, jwks_url) once they are written. The PUT writes all six
  // together; a 400's details.fields names each one it refused. Both need the
  // LMS admin scope: the read and write roles get a 403 (the connection itself,
  // and its `lti_registered` flag, they can read).
  getLtiRegistration: id => request('GET', `/admin/lms/connections/${id}/lti`),
  putLtiRegistration: (id, payload) => request('PUT', `/admin/lms/connections/${id}/lti`, payload),
  // The tool's own key (the one the platform trusts for what EdXR sends it),
  // never the package signing key above: -> the new active key { id, kid,
  // issued_at, previous_valid_until }.
  rotateLtiToolKey: id => request('POST', `/admin/lms/connections/${id}/lti/tool-key/rotate`),
  revokeLtiToolKey: id => request('POST', `/admin/lms/connections/${id}/lti/tool-key/revoke`),
  // Puts the connection's failed grade returns back in the queue to be sent again
  // (the LMS admin scope, a signed-in person: an API key is refused) -> {
  // requeued }, how many it queued. It is in the LMS service's OpenAPI as
  // LtiScoresResend, whose `requeued` is an integer; a page still reads it only
  // if it is a number. It sends again a grade that was queued, and a grade that
  // failed `line_item_not_found` also forgets its stale grade item address. It
  // does not recover `no_grade_passback` (nothing was queued) or `no_line_item`
  // (it reads the launch's same stored claim again): those grades are sent when
  // each learner launches the activity again, after the activity accepts grades
  // (OpenAPI `lti_score_failures`; D-132 R-B2). `requeued` does not count them.
  resendLtiScores: (id, reason) => request('POST', `/admin/lms/connections/${id}/lti/scores/resend`, reason === undefined ? undefined : { reason }),
  // Sends a session's result to the LMS again (the LMS admin scope, a signed-in
  // person, never an impersonated session), with the reason that goes in the audit
  // entry (3 to 500 characters) -> { result_rule, lti { requeued, note?, result_of? }, statements
  // { requeued }, scorm { pending_resync, superseded, result_of? }, nothing_to_resend }.
  resendSession: (ref, reason) => request('POST', `/admin/lms/sessions/${encodeURIComponent(ref)}/resend`, { reason }),
  // Puts a connection's failed xAPI statements back in the queue -> { requeued }.
  resendXapiStatements: (id, reason) => request('POST', `/admin/lms/connections/${id}/xapi/resend`, { reason }),
  // The system list (SYS_LMS_PRESET): every preset, with its config.
  listPresets: () => request('GET', '/admin/lms/presets'),
  // The published presets an organization's owner picks from: the fields the
  // picker shows, for any organization with the LMS feature.
  listPublishedPresets: () => request('GET', '/admin/lms/presets/published'),
  createPreset: payload => request('POST', '/admin/lms/presets', payload),
  patchPreset: (id, payload) => request('PATCH', `/admin/lms/presets/${id}`, payload),
  // Launcher versions (rapid-api-lms D-141): the released versions, the default,
  // each preset's pin and the pinned connections with what they run.
  getPlayer: () => request('GET', '/admin/lms/player'),
  // Pins a preset to a launcher version, or null to follow the default -> { id, name, pin }.
  setPresetPlayer: (presetId, version) => request('PUT', `/admin/lms/presets/${presetId}/player`, { version }),
  // Pins a connection, or null to clear -> { version, source, connection_pin, preset_pin }.
  setConnectionPlayer: (connectionId, version) =>
    request('PUT', `/admin/lms/connections/${connectionId}/player`, { version }),

  listIntegrations: nodeId => request('GET', `/admin/lms/courses/${nodeId}/integrations`),
  mapCourse: (connectionId, payload) => request('POST', `/admin/lms/connections/${connectionId}/courses`, payload),
  unmapCourse: (connectionId, nodeId) => request('DELETE', `/admin/lms/connections/${connectionId}/courses/${nodeId}`),
  // How the course's score and pass are reported on the connection (LMS
  // Phase F scoring): its own CourseScoring, or null to follow its preset's.
  // -> the mapping, with scoring (its own or null) and effective_scoring.
  setCourseScoring: (connectionId, nodeId, scoring) =>
    request('PATCH', `/admin/lms/connections/${connectionId}/courses/${nodeId}`, { scoring }),
  // Which result counts when a learner launches again: 'best' | 'latest' |
  // 'first', on its own PATCH -> the mapping (result_rule). 503
  // RESULT_RULE_UNAVAILABLE: the service's database is not updated for it yet.
  setCourseResultRule: (connectionId, nodeId, resultRule) =>
    request('PATCH', `/admin/lms/connections/${connectionId}/courses/${nodeId}`, { result_rule: resultRule }),
  buildPackage: (connectionId, nodeId) => request('POST', `/admin/lms/connections/${connectionId}/courses/${nodeId}/build`),
  packageDownloadURL: packageId =>
    request('GET', `/admin/lms/packages/${packageId}/download?redirect=false`).then(r => r.download_url),
  getBuildLog: packageId => request('GET', `/admin/lms/packages/${packageId}/build-log`),

  // The LMS's account links: those made through a connection, those in one
  // system key (a partner's slug or the LMS's own lms:<kind>:<issuer>).
  listLinks: ({ connectionId, systemKey, cursor } = {}) => {
    const params = new URLSearchParams()
    if (connectionId) params.set('connection_id', connectionId)
    if (systemKey) params.set('system_key', systemKey)
    if (cursor) params.set('cursor', cursor)
    const query = params.toString()
    return request('GET', `/admin/lms/account-links${query ? `?${query}` : ''}`)
  },
  createManualLink: payload => request('POST', '/admin/lms/account-links', payload),
  archiveLink: id => request('POST', `/admin/lms/account-links/${id}/archive`),
  // Revokes every link of the organization in the system key -> { system_key,
  // links_revoked } (a signed-in person with the LMS admin scope only).
  retireSystemKey: systemKey => request('POST', '/admin/lms/system-keys/retire', { system_key: systemKey }),

  listActiveSessions: ({ connectionId } = {}) =>
    request('GET', `/admin/lms/sessions/active${connectionId ? `?connection_id=${connectionId}` : ''}`),
  // A session is read by its ref, never its launch token (M2).
  getSession: sessionRef => request('GET', `/admin/lms/sessions/${encodeURIComponent(sessionRef)}`),

  listActivity: ({ eventType, since, cursor } = {}) => {
    const params = new URLSearchParams()
    if (eventType) params.set('event_type', eventType)
    if (since) params.set('since', since)
    if (cursor) params.set('cursor', cursor)
    const query = params.toString()
    return request('GET', `/admin/lms/activity${query ? `?${query}` : ''}`)
  },

  getUsage: () => request('GET', '/admin/lms/usage'),
  getDisplaySettings: () => request('GET', '/admin/lms/display-settings'),
  patchDisplaySettings: payload => request('PATCH', '/admin/lms/display-settings', payload),
  listConnectionCourses: connectionId => request('GET', `/admin/lms/connections/${connectionId}/courses`),

  // A core API key bound to this connection (Phase D D7-8): { name,
  // description, access: 'read-only' | 'read-write', expires_at } (null:
  // never) -> 201 { key, secret }, the secret shown once. The LMS's own API
  // keys and their six routes are retired; content lists, rotates and
  // revokes a connection's keys like any other API key.
  createConnectionKey: (connectionId, payload) => request('POST', `/admin/lms/connections/${connectionId}/keys`, payload)
}
